/**
 * Small sessionStorage-backed intent buffer so users can express "upgrade to
 * Pro" on the landing page, then complete sign-up and have Checkout fire
 * automatically without re-clicking.
 *
 * Why sessionStorage, not localStorage:
 *   - Cleared automatically when the tab closes — no stale intent leaks.
 *   - Same-tab persistence is exactly what OAuth round-trips need.
 *
 * Why not URL query params:
 *   - Intent would leak into browser history.
 *   - Better Auth's OAuth state param is already busy.
 *
 * Consumers should call `consumePostAuthIntent()` (read + clear) so the same
 * intent can't fire twice if the user navigates around the dashboard.
 */

const STORAGE_KEY = 'sendfast.postAuthIntent';

export type PostAuthIntent = { kind: 'checkout'; tier: 'pro' | 'scale' };

export function savePostAuthIntent(intent: PostAuthIntent): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(intent));
  } catch {
    /* sessionStorage disabled / quota exhausted — silently drop. */
  }
}

export function consumePostAuthIntent(): PostAuthIntent | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(STORAGE_KEY);
    const parsed = JSON.parse(raw) as Partial<PostAuthIntent>;
    if (
      parsed?.kind === 'checkout' &&
      (parsed.tier === 'pro' || parsed.tier === 'scale')
    ) {
      return { kind: 'checkout', tier: parsed.tier };
    }
    return null;
  } catch {
    return null;
  }
}
